import type { LogFn } from '../log.js';
import {
  abandonReconnectQuietWindow,
  beginDeferredDisconnectForReconnect,
  type ReconnectQuietSlot,
} from './reconnect-quiet-slot.js';
import {
  planReconnectAfterFailure,
  resetReconnectOutageTracker,
  type ReconnectOutageTracker,
} from './reconnect-outage-plan.js';

/** WebSocket-side channel (main bridge). */
export const BRIDGE_SERVICE_LABEL = '[Bridge service]' as const;
export const BRIDGE_DISCONNECT_LEAD_IN = 'Bridge connection was disconnected' as const;

/** Firehose / preview tunnel side channel. */
export const PREVIEW_TUNNEL_SERVICE_LABEL = '[Proxy and log service]' as const;
export const PREVIEW_TUNNEL_DISCONNECT_LEAD_IN = 'Preview tunnel connection was disconnected' as const;

const SHUTDOWN_DETAIL = 'Not reconnecting (shutting down).';
const RECONNECTING_DETAIL = 'Reconnecting…';

/**
 * Advance outage state, emit tier-promotion log when appropriate, return delay for the next attempt.
 */
export function applyTieredReconnectPlanAndLog(
  tracker: ReconnectOutageTracker,
  log: LogFn,
  serviceLabel: string,
  disconnectLeadIn: string,
  closeCode: number | undefined,
  closeReason: string | undefined,
): number {
  try {
    const { delayMs, logLine } = planReconnectAfterFailure(
      tracker,
      Date.now(),
      serviceLabel,
      disconnectLeadIn,
      closeCode,
      closeReason,
    );
    if (logLine) {
      try {
        log(logLine);
      } catch {
        /* never let logging break the reconnect loop */
      }
    }
    return delayMs;
  } catch {
    return 3000;
  }
}

export interface ArmReconnectDelayTimerOptions {
  delayMs: number;
  bumpAttempt: () => void;
  clearTimer: () => void;
  setTimer: (id: ReturnType<typeof setTimeout> | null) => void;
  shouldAbortBeforeRun: () => boolean;
  run: () => void;
  reschedule: () => void;
}

/**
 * Single shared pattern: bump attempt counter, arm timeout, run work, reschedule on sync throw.
 */
export function armReconnectDelayTimer(options: ArmReconnectDelayTimerOptions): void {
  const {
    delayMs,
    bumpAttempt,
    clearTimer,
    setTimer,
    shouldAbortBeforeRun,
    run,
    reschedule,
  } = options;
  clearTimer();
  bumpAttempt();
  setTimer(
    setTimeout(() => {
      setTimer(null);
      if (shouldAbortBeforeRun()) return;
      try {
        run();
      } catch {
        try {
          if (!shouldAbortBeforeRun()) {
            reschedule();
          }
        } catch {
          /* never propagate */
        }
      }
    }, delayMs),
  );
}

export interface BeginTieredSilentReconnectDisconnectOptions {
  isClosedByUser: () => boolean;
  quiet: ReconnectQuietSlot;
  code: number;
  reason: string;
  willReconnect: boolean;
  log: LogFn;
  serviceLabel: string;
  shouldAbortQuietWindow: () => boolean;
}

/** Deferred disconnect UI for a tiered auto-reconnect channel (no per-close spam while reconnecting). */
export function beginTieredSilentReconnectDisconnect(
  options: BeginTieredSilentReconnectDisconnectOptions,
): void {
  const {
    isClosedByUser,
    quiet,
    code,
    reason,
    willReconnect,
    log,
    serviceLabel,
    shouldAbortQuietWindow,
  } = options;
  beginDeferredDisconnectForReconnect({
    isClosedByUser,
    quiet,
    code,
    reason,
    willReconnect,
    log,
    serviceLabel,
    shutdownDetail: SHUTDOWN_DETAIL,
    reconnectingDetail: RECONNECTING_DETAIL,
    shouldAbortQuietWindow,
    silentWhileReconnect: willReconnect,
  });
}

export interface ClearTieredReconnectChannelOnOpenOptions {
  quiet: ReconnectQuietSlot;
  outage: ReconnectOutageTracker;
  clearLastCloseMeta: () => void;
  log: LogFn;
  restoredMessage: string;
}

/** After a successful open: drop quiet-window state, reset outage, optional recovery line. */
export function clearTieredReconnectChannelOnOpen(options: ClearTieredReconnectChannelOnOpenOptions): void {
  const { quiet, outage, clearLastCloseMeta, log, restoredMessage } = options;
  const hadOutage = outage.startedAt != null || outage.totalFailures > 0;
  abandonReconnectQuietWindow(quiet);
  resetReconnectOutageTracker(outage);
  clearLastCloseMeta();
  if (hadOutage) {
    try {
      log(restoredMessage);
    } catch {
      /* ignore */
    }
  }
}
